import { ChangeEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { CUSTOMER_TICKET_CATEGORIES, TicketCategory } from "@signalist/shared-types";

import { ReactComponent as MagnifyingGlass } from "@assets/icons/list-builder/magnifying-glass.svg";

import { ButtonSize } from "@/components/Button/types";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { InputField } from "@/components/InputField/InputField";

import { SupportTicketsController } from "../hooks/useSupportTickets";

/** Scope values of the company-admin toggle; a plain user never sees it. */
const SCOPE_COMPANY = "COMPANY";
const SCOPE_MINE = "MINE";

/**
 * Search, category and — for a company admin — the "all of the company / only mine" scope, plus
 * the sentence that explains why an admin sees colleagues' tickets. Presentational: every piece of
 * state lives in `useSupportTickets`.
 */
export const SupportFilterBar: FunctionComponent<{ controller: SupportTicketsController }> = ({
	controller,
}) => {
	const { t } = useTranslation();

	const categoryOptions: AutoCompleteItem[] = [
		{ id: null, title: t("support.filters.categoryAll") },
		...CUSTOMER_TICKET_CATEGORIES.map((value) => ({
			id: value,
			title: t(`support.category.${value}`),
		})),
	];

	const scopeOptions: AutoCompleteItem[] = [
		{
			id: SCOPE_COMPANY,
			title: t("support.filters.scopeCompany", { company: controller.companyName }),
		},
		{ id: SCOPE_MINE, title: t("support.filters.scopeMine") },
	];

	const selectedCategory =
		categoryOptions.find((option) => option.id === controller.category) ?? categoryOptions[0];

	const selectedScope = controller.onlyMine ? scopeOptions[1] : scopeOptions[0];

	return (
		<div className="flex h-11 items-center gap-2 border-b border-border-light bg-white px-4">
			<div className="w-[220px] flex-shrink-0">
				<InputField
					className="!rounded-[10px]"
					classNameWrapper="!mb-0"
					handleChange={(event: ChangeEvent<HTMLInputElement>) =>
						controller.setSearch(event.target.value)
					}
					iconInside={<SvgIcon className="text-gray-500" svgIcon={MagnifyingGlass} />}
					name="search"
					placeholder={t("support.filters.searchPlaceholder")}
					size={ButtonSize.XS}
					value={controller.search}
				/>
			</div>

			<div className="flex-shrink-0">
				<Dropdown
					classNameDropdown="w-max min-w-[180px]"
					data={categoryOptions}
					defaultValue={categoryOptions[0]}
					floating={true}
					handleSelect={(value: AutoCompleteItem) =>
						controller.setCategory((value.id as TicketCategory | null) ?? null)
					}
					size={ButtonSize.XS}
					value={selectedCategory}
				/>
			</div>

			{controller.isCompanyAdmin && (
				<>
					<div className="flex-shrink-0">
						<Dropdown
							classNameDropdown="w-max min-w-[180px]"
							data={scopeOptions}
							defaultValue={scopeOptions[0]}
							floating={true}
							handleSelect={(value: AutoCompleteItem) =>
								controller.setOnlyMine(value.id === SCOPE_MINE)
							}
							size={ButtonSize.XS}
							value={selectedScope}
						/>
					</div>
					<span className="ml-auto text-xs text-text-meta">
						{t("support.filters.companyAdminHint")}
					</span>
				</>
			)}
		</div>
	);
};
